import { confirmBox } from '../components/dialog.js';
import { renderHeader } from '../components/header.js';
import { loadDayExercises, loadLastSets, startSession, saveSet, updateSet, deleteSet, deleteSession, finishSession, updateProgramExercise, loadLiftNotes, loadSettings, saveSettings, addProgramExercise, deleteProgramExercise, reorderProgramExercises } from '../lib/grit-data.js';
import { openExercisePicker } from '../components/exercise-picker.js';
import { chooseBox } from '../components/dialog.js';
import { isPending } from '../lib/db.js';
import { displayName, escapeHtml } from '../lib/exercise-names.js';
import { buildExerciseCard, summarizeFinish, isPersonalRecord } from '../lib/session-view.js';
import { progressSets, needsRirAnswer, WARMUP_REST_SEC } from '../lib/bls-engine.js';
import { phaseInfo, phaseLabel, localISO } from '../lib/phase.js';
import { startRest, stopRest } from '../components/rest-timer.js';
import { openSwap } from '../components/swap-sheet.js';
import { openLiftForm } from '../components/lift-form.js';
import { openNotes } from '../components/notes-sheet.js';
import { notesByLift, noteDate } from '../lib/lift-notes.js';
import { toast } from '../components/toast.js';
import { isViewingSelf } from '../lib/auth.js';
import { saveProgress, loadProgress, clearProgress } from '../lib/progress.js';
import { icon } from '../components/icons.js';
import { go } from '../router.js';
import { repRange } from '../lib/formatters.js';

const fmtRest = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
const KNEE = [['none', 'Pain-free'], ['mild', 'Bit achy'], ['sharp', 'Sharp']];
const todayISO = () => localISO();
const fmtHM = (ms) => { const d = new Date(ms); return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`; };
function niceOutBy(hhmm) { const [h, m] = hhmm.split(':').map(Number); const d = new Date(); d.setHours(h, m, 0, 0); return fmtHM(d.getTime()); }

const ICON = {
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5"><polyline points="20 6 9 17 4 12"/></svg>',
  back: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><polyline points="15 18 9 12 15 6"/></svg>',
  close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
  clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" class="w-3.5 h-3.5"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
  more: '<svg viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>',
  plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
  timer: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5"><circle cx="12" cy="13" r="8"/><path d="M12 9.5V13l2 2"/><path d="M9 2h6"/></svg>',
};

// Tear down a previous logger mount (intervals + listeners) so a same-hash
// remount (back-to-self pill, cross-tab storage event) never stacks a second one.
let loggerTeardown = null;

export function renderLogger(el) {
  const dayId = sessionStorage.getItem('grit-day');
  if (!dayId) { go('home'); return; }
  if (loggerTeardown) { loggerTeardown(); loggerTeardown = null; }

  const readOnly = !isViewingSelf();
  let cards = [];        // [{ pe, card, result, swappedFrom, kneeFeel }]
  let notesByEx = {};    // exercise_id -> that lift's notes, newest first
  let sessionId = null;
  let current = 0;
  let elapsedMs = 0;        // accumulated active workout time
  let runningSince = null;  // timestamp while the timer runs, else null (paused)
  let targetDate = todayISO();
  let finished = false;  // suppress progress writes once we're wrapping up
  let sessionNote = '';  // the day's note: sleep, aches, felt strong (book p.345)
  let phase = null;      // { phase, week, deload } from settings.phase_start

  el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-5"><p class="text-inksoft">Loading…</p></div>`;

  const saved = readOnly ? null : loadProgress();
  if (saved && saved.dayId === dayId && Array.isArray(saved.cards) && saved.cards.length) {
    // Resume exactly where we left off (item 1).
    cards = saved.cards;
    cards.forEach((e) => { e.card.name = displayName(e.pe.exercise) || e.card.name; }); // resolve personal names
    sessionId = saved.sessionId ?? null;
    current = Math.min(saved.current || 0, cards.length - 1);
    elapsedMs = saved.elapsedMs || 0;
    runningSince = null; // resume paused; a saved timestamp could span unknown dead time
    targetDate = saved.date || todayISO();
    sessionNote = saved.note || '';
    phase = saved.phase || null;
    paint();
  } else {
    // Fresh start. A back-dated make-up passes its date via sessionStorage (item 6).
    targetDate = sessionStorage.getItem('grit-day-date') || todayISO();
    sessionStorage.removeItem('grit-day-date');
    (async () => {
      const [exs, settings] = await Promise.all([loadDayExercises(dayId), readOnly ? Promise.resolve({}) : loadSettings()]);
      phase = phaseInfo(settings.phase_start, targetDate);
      const deload = !!phase?.deload;
      const lasts = await Promise.all(exs.map((e) => loadLastSets(e.exercise_id, undefined, dayId)));
      // Warm up before the first lift for each major muscle group today (p.330).
      const seenGroup = new Set();
      const warmFor = exs.map((pe) => {
        const g = pe.exercise?.muscle_group;
        if ((pe.tracking || 'weight') !== 'weight' || !g || g === 'abs' || g === 'calves' || seenGroup.has(g)) return false;
        seenGroup.add(g); return true;
      });
      cards = exs.map((pe, i) => ({ pe, card: buildExerciseCard({ ...pe, exercise: pe.exercise }, lasts[i], { warmup: warmFor[i] && !deload, deload }), result: null, swappedFrom: null, kneeFeel: null, askRir: null }));
      cards.forEach((e) => { e.card.name = displayName(e.pe.exercise) || e.card.name; }); // resolve personal names
      persist();
      paint();
    })();
  }

  // Active notes for these lifts (recur until dismissed). Load once, then repaint.
  const reloadNotes = () => loadLiftNotes().then((rows) => { notesByEx = notesByLift(rows); paint(); });
  reloadNotes();

  // ── Workout timer ──────────────────────────────────────────────────────────
  // Counts active workout time. Start/Pause is manual (and auto-starts on the
  // first logged set). It pauses when you leave the logger or background the app,
  // so leaving and resuming later never inflates the length.
  function totalElapsed() { return elapsedMs + (runningSince ? Date.now() - runningSince : 0); }
  function fmtTimer(ms) {
    const s = Math.floor(ms / 1000), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
    return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}` : `${m}:${String(ss).padStart(2, '0')}`;
  }
  function timerBtnClass(running) { return `flex-none rounded-full px-5 min-h-[44px] text-[15px] font-semibold active:scale-95 transition-transform ${running ? 'bg-fill text-ink' : 'bg-grit text-white'}`; }
  function timerBtnLabel() { return runningSince ? 'Pause' : (totalElapsed() > 0 ? 'Resume' : 'Start'); }
  function startTimer() { if (finished || runningSince) return; runningSince = Date.now(); persist(); updateTimerUI(); }
  function pauseTimer() { if (runningSince) { elapsedMs += Date.now() - runningSince; runningSince = null; } persist(); updateTimerUI(); }
  function updateTimerUI() {
    const t = document.getElementById('grit-timer'); if (t) t.textContent = fmtTimer(totalElapsed());
    const b = document.getElementById('timer-toggle'); if (b) { b.textContent = timerBtnLabel(); b.className = timerBtnClass(!!runningSince); }
  }
  function timerBar() {
    if (readOnly) return '';
    return `<div class="flex items-center justify-between grit-card pl-4 pr-2 py-2 mb-3">
      <div class="flex items-baseline gap-2"><span class="text-[15px] text-inksoft">Workout</span><span id="grit-timer" class="font-rounded text-[22px] font-semibold text-ink tabular-nums">${fmtTimer(totalElapsed())}</span></div>
      <button id="timer-toggle" class="${timerBtnClass(!!runningSince)}">${timerBtnLabel()}</button>
    </div>`;
  }

  // Tick the out-by session clock live, then self-clean when we finish or leave.
  let clockSeen = false;
  const clockTimer = setInterval(() => {
    if (finished) { clearInterval(clockTimer); return; }
    const node = document.getElementById('grit-clock');
    if (node) { clockSeen = true; node.outerHTML = sessionClock(); return; }
    if (clockSeen || !localStorage.getItem('grit-pref-outby')) clearInterval(clockTimer);
  }, 20000);
  // Live 1-second tick: fold the elapsed second into elapsedMs and persist, so a
  // hard kill loses at most ~1s and never counts dead time as workout time.
  const timerTick = setInterval(() => {
    if (finished) { clearInterval(timerTick); return; }
    if (runningSince) { elapsedMs += Date.now() - runningSince; runningSince = Date.now(); persist(); updateTimerUI(); }
  }, 1000);
  function teardownTimers() { clearInterval(clockTimer); clearInterval(timerTick); }

  // Pause + save when the app is backgrounded; resume when it returns.
  let wasRunning = false;
  const onVis = () => {
    if (finished) return;
    if (document.hidden) { wasRunning = !!runningSince; pauseTimer(); }
    else if (wasRunning) { wasRunning = false; startTimer(); }
  };
  document.addEventListener('visibilitychange', onVis);
  // Pause + save when navigating away from the logger by any route.
  const onHashLeave = () => {
    if (location.hash !== '#/logger') { pauseTimer(); teardown(); }
  };
  window.addEventListener('hashchange', onHashLeave);
  // One teardown for this mount; called on leave, finish, OR a same-hash remount.
  function teardown() {
    teardownTimers();
    window.removeEventListener('hashchange', onHashLeave);
    document.removeEventListener('visibilitychange', onVis);
    loggerTeardown = null;
  }
  loggerTeardown = teardown;

  function persist() {
    if (readOnly || finished) return;
    saveProgress({ dayId, date: targetDate, sessionId, current, elapsedMs, runningSince, cards, note: sessionNote, phase });
  }

  // Save & leave without finishing: the workout stays resumable from the home tab.
  function handleLeave() {
    pauseTimer();
    const anyDone = cards.some((e) => e.card.sets.some((s) => s.done));
    if (!sessionId && !anyDone) clearProgress();
    go('home');
  }

  function workSetsOf(entry) { return entry.card.sets.filter((s) => !s.isWarmup); }

  // "Out by 6:05" session clock: live projected finish vs the target time.
  function sessionClock() {
    const outBy = localStorage.getItem('grit-pref-outby');
    if (readOnly || !outBy || !cards.length) return '';
    const totalWork = cards.reduce((n, e) => n + workSetsOf(e).length, 0);
    const doneWork = cards.reduce((n, e) => n + workSetsOf(e).filter((s) => s.done).length, 0);
    const [h, m] = outBy.split(':').map(Number);
    const target = new Date(); target.setHours(h, m, 0, 0);
    let right; let tone;
    if (!doneWork || !totalWork) {
      right = 'start a set to project'; tone = 'bg-grit-soft text-grit-press';
    } else {
      const now = Date.now();
      const perSet = totalElapsed() / doneWork;
      const proj = now + Math.max(0, totalWork - doneWork) * perSet;
      const late = proj > target.getTime();
      tone = late ? 'bg-warn-tint text-warn' : 'bg-success-tint text-success';
      right = `done ~${fmtHM(proj)} · ${late ? 'behind' : 'on track'}`;
    }
    return `<div id="grit-clock" class="${tone} rounded-xl px-3 py-2 mb-3 text-[13px] font-bold flex items-center justify-between">
      <span>Out by ${niceOutBy(outBy)}</span><span>${right}</span></div>`;
  }

  // Colour follows the advice: green = weight goes up or sticks, amber = back off or
  // wait, neutral = keep going.
  function resultChip(entry) {
    const r = entry.result;
    if (!r) return '';
    const good = ['bump', 'locked'], caution = ['shave', 'revert', 'under', 'no_rir', 'pain'];
    const tone = good.includes(r.action) ? 'bg-success-tint text-success'
      : caution.includes(r.action) ? 'bg-warn-tint text-[#7A570F]'
      : 'bg-fill text-ink';
    const pr = isPersonalRecord(entry.card)
      ? `<div class="grit-chip mt-2 bg-[#FBF3E0] text-[#7A570F]">${icon.check('w-3.5 h-3.5')} New personal best</div>`
      : '';
    return `<div class="${tone} rounded-2xl px-4 py-3 mt-4 text-[15px] font-semibold leading-snug" role="status">${r.message}</div>${pr}`;
  }

  function exerciseStrip() {
    return `<div class="flex gap-2 overflow-x-auto pb-1 -mx-5 px-5" style="scrollbar-width:none">
      ${cards.map((e, i) => {
        const work = workSetsOf(e);
        const allDone = work.length && work.every((s) => s.done);
        const active = i === current;
        const base = active ? 'bg-ink text-white' : allDone ? 'bg-success-tint text-success' : 'bg-card text-ink';
        return `<button data-jump="${i}" aria-label="Exercise ${i + 1}" class="flex-none rounded-full min-w-[44px] min-h-[44px] px-3 text-[15px] font-semibold inline-flex items-center justify-center gap-1 active:scale-95 transition-transform ${base}">${i + 1}${allDone && !active ? icon.check('w-3.5 h-3.5') : ''}</button>`;
      }).join('')}
    </div>`;
  }

  function paint() {
    if (!cards.length) return;
    const entry = cards[current];
    const c = entry.card;
    const tracking = c.tracking || 'weight';
    const isTime = tracking === 'time';
    const showWeight = tracking === 'weight' || (tracking === 'bodyweight' && c.addWeight);
    let workNo = 0;
    const setRows = c.sets.map((s, si) => {
      const removable = !readOnly && !s.done && c.sets.length > 1;
      const setLabel = s.isWarmup ? 'W' : String(++workNo);
      return `
      <div class="flex items-center gap-2 mt-2.5">
        ${tracking === 'weight' && !readOnly
          ? `<button data-warm="${si}" aria-label="${s.isWarmup ? 'Warm-up set, tap to make it a work set' : `Set ${setLabel}, tap to mark as warm-up`}" class="w-8 h-11 flex-none flex items-center justify-center rounded-lg text-[15px] font-semibold ${s.isWarmup ? 'text-grit' : 'text-inksoft'} active:bg-fill">${setLabel}</button>`
          : `<div class="w-8 flex-none text-center text-[15px] font-semibold ${s.isWarmup ? 'text-grit' : 'text-inksoft'}">${setLabel}</div>`}
        ${showWeight ? `<input data-s="${si}" data-f="weight" type="number" inputmode="decimal" value="${s.weight ?? ''}"
               ${readOnly ? 'disabled' : ''} placeholder="lb" aria-label="Weight for set ${setLabel}" class="grit-input flex-1 min-w-0 text-center !px-1 !text-[20px] font-semibold tabular-nums" />` : ''}
        <input data-s="${si}" data-f="reps" type="number" inputmode="numeric" value="${s.reps ?? ''}"
               ${readOnly ? 'disabled' : ''} placeholder="${isTime ? 'sec' : s.isWarmup ? 'reps' : repRange(c.repLow, c.repHigh).replace('–', '-')}" aria-label="Reps for set ${setLabel}" class="grit-input flex-1 min-w-0 text-center !px-1 !text-[20px] font-semibold tabular-nums" />
        ${isTime && !readOnly ? `<button data-timer="${si}" aria-label="Start countdown" class="flex-none w-11 h-11 rounded-full bg-grit-soft text-grit flex items-center justify-center active:scale-95">${ICON.timer}</button>` : ''}
        <button data-check="${si}" ${readOnly ? 'disabled' : ''} aria-label="${s.done ? 'Logged, tap to undo' : 'Log this set'}"
          class="relative flex-none w-11 h-11 rounded-full flex items-center justify-center active:scale-90 transition-transform ${s.done ? 'bg-success text-white' : 'bg-card border-2 border-[rgba(60,60,67,0.25)] text-inksoft'}">${s.done ? ICON.check : ''}${s.done && isPending(s.savedId) ? `<span data-pend="${s.savedId}" aria-label="Saved on your phone, sends when you have signal" class="absolute -right-1 -bottom-1 w-5 h-5 rounded-full bg-[#FBF3E0] text-[#7A570F] flex items-center justify-center">${ICON.clock}</span>` : ''}</button>
        ${removable ? `<button data-remove="${si}" aria-label="Remove set" class="flex-none w-7 h-11 -mr-1 flex items-center justify-center text-inksoft">${ICON.close}</button>` : (readOnly ? '' : '<span class="w-7 -mr-1 flex-none"></span>')}
      </div>`;
    }).join('');
    const addWeightToggle = (tracking === 'bodyweight' && !readOnly)
      ? `<button id="toggle-weight" class="w-full mt-3 py-2 rounded-xl text-[13px] font-bold ${c.addWeight ? 'text-inksoft' : 'text-grit'}">${c.addWeight ? 'Bodyweight only' : '+ Add weight'}</button>`
      : '';

    const askSet = entry.askRir != null ? c.sets[entry.askRir] : null;
    const rirPrompt = (askSet && !readOnly) ? `
      <div class="rounded-2xl bg-grit-soft p-4 mt-4">
        <div class="text-[15px] font-semibold text-ink">${askSet.reps} reps. Could you have done at least one more good rep?</div>
        <div class="text-[15px] text-inksoft mt-1">On a big lift, the jump only counts with a rep or two left in the tank (p.329).</div>
        <div class="flex gap-2 mt-3">
          <button data-rir="1" class="flex-1 grit-btn !py-3 !text-[15px]">Yes, 1+ left</button>
          <button data-rir="0" class="flex-1 grit-btn-quiet !py-3 !text-[15px]">No, that was it</button>
        </div>
      </div>` : '';

    const kneeCheck = (c.knee && !readOnly) ? `
      <div class="mt-4">
        <div class="text-[13px] font-bold text-inksoft mb-1.5">How did the knees feel?</div>
        <div class="flex gap-2">
          ${KNEE.map(([k, lbl]) => `<button data-knee="${k}" class="flex-1 rounded-full min-h-[44px] text-[15px] font-semibold active:scale-95 transition-transform ${entry.kneeFeel === k ? (k === 'sharp' ? 'bg-danger-tint text-danger' : k === 'mild' ? 'bg-warn-tint text-warn' : 'bg-success-tint text-success') : 'bg-fill text-ink'}">${lbl}</button>`).join('')}
        </div>
      </div>` : '';

    const phaseNote = phase ? (phase.deload
      ? '<div class="rounded-2xl bg-grit-soft text-grit-press text-[15px] font-semibold px-4 py-3 mb-3">Deload week: 2 sets per lift at your normal weight, 2 reps on big lifts and 4 on the rest. Easy cardio only (p.332).</div>'
      : `<div class="text-[15px] font-medium text-inksoft mb-2">${phaseLabel(phase)}</div>`) : '';
    const dateNote = targetDate !== todayISO()
      ? `<div class="bg-warn-tint text-warn text-[13px] font-bold rounded-xl px-3 py-2 mb-3">Logging for ${new Date(targetDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>`
      : '';

    const exName = displayName(entry.pe.exercise) || c.name;
    // The lift's newest note shows on its card every time; tap to see, add, edit or delete them all.
    const liftNotes = notesByEx[entry.pe.exercise_id] || [];
    const note = liftNotes[0];
    const noteBlock = note ? `<button data-notes class="w-full text-left bg-warn-tint text-warn rounded-xl px-3 py-2.5 mt-3 active:scale-[0.99] transition-transform">
            <span class="block text-[12px] font-semibold opacity-80">${noteDate(note.date || note.created_at)}${liftNotes.length > 1 ? ` · ${liftNotes.length} notes` : ''}</span>
            <span class="block text-[15px] font-bold whitespace-pre-line">${escapeHtml(note.body)}</span>
          </button>` : (readOnly ? '' : '<button data-notes class="mt-2 min-h-[44px] text-[15px] font-semibold text-grit-press">Add a note for this lift</button>');

    el.innerHTML = `
      ${renderHeader()}
      <div class="grit-page px-5 pt-3">
        <button id="back" class="flex items-center gap-1 text-grit text-[17px] min-h-[44px] -ml-1 mb-1">${ICON.back} Save &amp; exit</button>
        ${readOnly ? '<div class="bg-grit-soft text-grit-press text-[15px] font-semibold rounded-xl px-3 py-2 mb-3">Viewing another athlete, read only</div>' : dateNote}
        ${readOnly ? '' : phaseNote}
        ${timerBar()}
        ${sessionClock()}
        ${exerciseStrip()}
        <div class="text-[13px] text-inksoft mt-3">Exercise ${current + 1} of ${cards.length}</div>
        <div class="grit-card p-4 mt-1">
          <div class="flex items-center justify-between gap-2">
            <div class="grit-section">${escapeHtml(exName)}</div>
            ${readOnly ? '' : `<div class="flex items-center gap-1.5 flex-none"><button data-swap class="grit-pill">Swap</button><button data-more aria-label="More for this lift" class="grit-pill !px-2.5">${ICON.more}</button></div>`}
          </div>
          <${readOnly ? 'div' : 'button data-edit-lift aria-label="Change reps and rest"'} class="flex items-center gap-2 mt-2 text-left min-h-[32px]">
            <span class="grit-chip-accent">${repRange(c.repLow, c.repHigh)} ${isTime ? 'sec' : 'reps'}</span>
            <span class="grit-chip bg-fill text-ink">Rest ${fmtRest(c.restSeconds)}</span>
            ${readOnly ? '' : '<span class="text-[13px] font-semibold text-grit-press">Edit</span>'}
          </${readOnly ? 'div' : 'button'}>
          ${noteBlock}
          <div class="text-[15px] text-inksoft mt-3">Last time: <span class="font-semibold text-ink">${c.lastSummary}</span></div>
          <div class="text-[15px] text-inksoft mt-1 leading-snug">${c.coach}</div>
          ${setRows}
          ${addWeightToggle}
          ${readOnly ? '' : `<button id="addset" class="w-full mt-3 min-h-[44px] rounded-full text-grit text-[15px] font-semibold flex items-center justify-center gap-1.5 active:bg-fill">${ICON.plus} Add set</button>`}
          ${rirPrompt}
          ${kneeCheck}
          ${askSet ? '' : resultChip(entry)}
        </div>
        <div class="flex items-center gap-3 mt-4">
          <button id="prev" ${current === 0 ? 'disabled' : ''} class="grit-btn-quiet flex-1 disabled:opacity-30">${icon.left('w-4 h-4')} Previous</button>
          <button id="next" ${current === cards.length - 1 ? 'disabled' : ''} class="grit-btn-quiet flex-1 disabled:opacity-30">Next ${icon.right('w-4 h-4')}</button>
        </div>
        ${readOnly ? '' : `<button id="add-lift" class="grit-btn-quiet mt-3 !text-grit">${ICON.plus} Add a lift</button>`}
        ${readOnly ? '' : `<label class="block mt-4"><span class="grit-eyebrow block mb-1.5">Today's note</span>
          <textarea id="session-note" rows="2" maxlength="500" placeholder="Sleep, aches, felt strong. A lift\'s own notes go on that lift." class="grit-input !py-3 resize-none">${escapeHtml(sessionNote)}</textarea></label>`}
        ${readOnly ? '' : '<button id="finish" class="grit-btn-dark mt-3">Finish workout</button>'}
        <div class="h-32"></div>
      </div>`;

    el.querySelectorAll('input[data-f]').forEach((inp) => {
      inp.oninput = () => {
        const si = +inp.dataset.s;
        cards[current].card.sets[si][inp.dataset.f] = inp.value === '' ? null : Number(inp.value);
        cards[current].card.sets[si].userEdited = true; // don't let auto-fill clobber this
        persist();
      };
      inp.onchange = () => {
        const si = +inp.dataset.s;
        const s = cards[current].card.sets[si];
        if (s.done && s.savedId) handleEdit(si);
      };
    });
    el.querySelectorAll('[data-check]').forEach((b) => { b.onclick = () => handleCheck(+b.dataset.check); });
    el.querySelectorAll('[data-timer]').forEach((b) => { b.onclick = () => handleSetTimer(+b.dataset.timer); });
    el.querySelectorAll('[data-warm]').forEach((b) => { b.onclick = () => handleWarm(+b.dataset.warm); });
    const tw = document.getElementById('toggle-weight');
    if (tw) tw.onclick = () => { c.addWeight = !c.addWeight; if (!c.addWeight) c.sets.forEach((s) => { if (!s.done) s.weight = null; }); persist(); paint(); };
    el.querySelectorAll('[data-remove]').forEach((b) => { b.onclick = () => handleRemove(+b.dataset.remove); });
    el.querySelectorAll('[data-jump]').forEach((b) => { b.onclick = () => { current = +b.dataset.jump; persist(); paint(); }; });
    el.querySelectorAll('[data-knee]').forEach((b) => { b.onclick = () => { entry.kneeFeel = b.dataset.knee; recomputeResult(entry); persist(); paint(); }; });
    el.querySelectorAll('[data-rir]').forEach((b) => { b.onclick = () => handleRir(Number(b.dataset.rir)); });
    const noteEl = document.getElementById('session-note');
    if (noteEl) noteEl.oninput = () => { sessionNote = noteEl.value; persist(); };
    const addset = document.getElementById('addset'); if (addset) addset.onclick = () => handleAddSet();
    const prev = document.getElementById('prev'); if (prev) prev.onclick = () => { if (current > 0) { current--; persist(); paint(); } };
    const next = document.getElementById('next'); if (next) next.onclick = () => { if (current < cards.length - 1) { current++; persist(); paint(); } };
    const swap = document.querySelector('[data-swap]'); if (swap) swap.onclick = () => handleSwap();
    const editLift = document.querySelector('[data-edit-lift]'); if (editLift) editLift.onclick = () => handleEditLift();
    const more = document.querySelector('[data-more]'); if (more) more.onclick = () => handleMore();
    const addLift = document.getElementById('add-lift'); if (addLift) addLift.onclick = () => handleAddLift();
    const notesBtn = el.querySelector('[data-notes]');
    if (notesBtn) notesBtn.onclick = () => { const e = cards[current]; openNotes({ title: displayName(e.pe.exercise) || e.card.name, lifts: [{ exercise_id: e.pe.exercise_id, exercise: e.pe.exercise }], date: targetDate, readOnly, onChange: reloadNotes }); };
    const fin = document.getElementById('finish'); if (fin) fin.onclick = () => handleFinish({ confirm: true });
    const tt = document.getElementById('timer-toggle'); if (tt) tt.onclick = () => { if (runningSince) pauseTimer(); else startTimer(); };
    document.getElementById('back').onclick = () => handleLeave();
  }

  // Countdown for a timed set (planks etc.): reuse the rest-timer ring + beep.
  function handleSetTimer(si) {
    const c = cards[current].card;
    const secs = Number(c.sets[si]?.reps) || c.repHigh || 60;
    startRest({ label: c.name, seconds: secs, title: `${c.name} · hold`, doneLabel: "Time's up" });
  }

  // Run the book's progression over this lift's logged work sets (p.327-329), show the
  // result, set the next un-logged sets to the engine's weight, and save where this
  // lift starts next time on this workout day.
  function recomputeResult(entry) {
    const c = entry.card;
    if ((c.tracking || 'weight') !== 'weight' || c.deload) { entry.result = null; return; }
    const painFlag = entry.kneeFeel === 'sharp' ? 'sharp' : entry.kneeFeel === 'mild' ? 'mild' : undefined;
    const done = c.sets.filter((x) => x.done && !x.isWarmup);
    const pending = entry.askRir != null;
    const r = progressSets({
      repLow: c.repLow, repHigh: c.repHigh, increment: c.increment, isPrimary: c.isPrimary,
      required: c.required, startWeight: c.startWeight, carryFrom: c.carryFrom, painFlag,
      sets: done.map((x) => ({ weight: x.weight, reps: x.reps, rir: x.rir })),
    });
    entry.engine = r;
    entry.result = (r.last && !pending) ? {
      decision: r.nextWeight > c.startWeight ? 'go_up' : r.nextWeight < c.startWeight ? 'drop' : 'stay',
      fromWeight: c.startWeight, toWeight: r.nextWeight, message: r.last.message, action: r.last.action,
    } : null;
    if (pending) return;
    // Every set after the last logged one starts at the engine's weight, unless typed by hand.
    const lastDone = c.sets.reduce((m, x, i) => (x.done ? i : m), -1);
    c.sets.forEach((x, i) => { if (i > lastDone && !x.done && !x.isWarmup && !x.userEdited && r.nextWeight != null) x.weight = r.nextWeight; });
    saveEngineState(entry, done.length ? r : null);
  }

  // Where this lift starts next time (only for the planned lift, not a one-day swap).
  function saveEngineState(entry, r) {
    if (readOnly || !entry.pe.id || (entry.swappedFrom && !entry.madeDefault)) return;
    const c = entry.card;
    const fields = r
      ? { start_weight_lb: r.nextWeight, progression_mode: String(r.required), carry_from_lb: r.carryFrom }
      : { start_weight_lb: c.startWeight, progression_mode: String(c.required), carry_from_lb: c.carryFrom };
    const key = JSON.stringify(fields);
    if (entry.savedEngine === key) return;
    entry.savedEngine = key;
    updateProgramExercise(entry.pe.id, fields).then((ok) => { if (!ok) entry.savedEngine = null; });
  }

  async function handleRir(rir) {
    const entry = cards[current];
    const si = entry.askRir;
    if (si == null) return;
    const s = entry.card.sets[si];
    s.rir = rir;
    entry.askRir = null;
    const before = entry.engine?.nextWeight;
    recomputeResult(entry);
    persist();
    paint();
    if (entry.result?.action === 'bump' && entry.engine?.nextWeight !== before) toast(`+${entry.card.increment} lb. Next set ${entry.engine.nextWeight}.`);
    if (s.savedId) { const ok = await updateSet(s.savedId, { rir }); if (!ok) toast("Couldn't save that, try again", { tone: 'bad' }); }
  }

  async function ensureSession() {
    if (sessionId) return sessionId;
    sessionId = await startSession(dayId, targetDate);
    if (!sessionId) { toast("Couldn't start, check your connection", { tone: 'bad' }); return null; }
    // The first logged workout starts Phase 1 (9-week phases, p.314).
    loadSettings().then((st) => { if (!st.phase_start) saveSettings({ phase_start: targetDate }); });
    persist();
    return sessionId;
  }

  async function handleCheck(si) {
    const entry = cards[current];
    const s = entry.card.sets[si];

    if (s.done) { // uncheck -> remove the logged row
      s.done = false;
      s.rir = undefined;
      if (entry.askRir === si) entry.askRir = null;
      const id = s.savedId; s.savedId = null;
      recomputeResult(entry);
      persist();
      paint();
      if (id) await deleteSet(id);
      return;
    }

    if (!(await ensureSession())) return;
    if (s.reps == null || s.reps === '') { toast(entry.card.tracking === 'time' ? 'Type the seconds first' : 'Type your reps first', { tone: 'info' }); return; }
    startTimer(); // auto-start the clock on the first logged set
    s.done = true;
    // Big lift at the top of the range: ask about reps left before it counts (p.329).
    if (entry.card.tracking === 'weight' && !entry.card.deload && needsRirAnswer(s, entry.card)) entry.askRir = si;
    // Rest only while sets remain anywhere in the workout; the book rests between sets AND between
    // lifts (p.320), never after the last one. "Next" is this lift until its sets are all checked.
    const left = (e) => e.card.sets.some((x) => !x.done);
    if (cards.some(left)) {
      const nextEntry = left(entry) ? entry : (cards.slice(current + 1).find(left) || cards.find(left));
      startRest({ label: entry.card.name, next: nextEntry.card.name, seconds: s.isWarmup ? WARMUP_REST_SEC : entry.card.restSeconds });
    } else {
      stopRest();
    }
    const before = entry.engine?.nextWeight;
    recomputeResult(entry);
    if (entry.result?.action === 'bump' && entry.engine?.nextWeight !== before) toast(`+${entry.card.increment} lb. Next set ${entry.engine.nextWeight}.`);
    persist();
    paint();

    const id = await saveSet(sessionId, {
      exerciseId: entry.pe.exercise_id, setNumber: si + 1,
      weight: s.weight, reps: s.reps, rir: s.rir, isWarmup: s.isWarmup, swappedFrom: entry.swappedFrom,
    });
    if (!id) { s.done = false; recomputeResult(entry); persist(); paint(); toast("Set didn't save, tap it again", { tone: 'bad' }); }
    else { s.savedId = id; persist(); if (isPending(id)) paint(); } // show the "waiting to send" clock
  }

  // Non-destructive edit of a set that's already logged (item 3).
  async function handleEdit(si) {
    const entry = cards[current];
    const s = entry.card.sets[si];
    recomputeResult(entry);
    persist();
    paint();
    const ok = await updateSet(s.savedId, { weight: s.weight, reps: s.reps, rir: s.rir });
    if (!ok) toast("Edit didn't save, try again", { tone: 'bad' });
  }

  async function handleWarm(si) {
    const entry = cards[current];
    const s = entry.card.sets[si];
    s.isWarmup = !s.isWarmup;
    toast(s.isWarmup ? "Warm-up set. It won't count toward your weight bumps." : 'Back to a working set.');
    recomputeResult(entry);
    persist();
    paint();
    if (s.done && s.savedId) {
      const ok = await updateSet(s.savedId, { isWarmup: s.isWarmup });
      if (!ok) toast("Couldn't update, try again", { tone: 'bad' });
    }
  }

  function handleAddSet() {
    const sets = cards[current].card.sets;
    const last = sets[sets.length - 1] || {};
    sets.push({ setNumber: sets.length + 1, weight: last.weight ?? null, reps: last.reps ?? null, done: false, isWarmup: false });
    persist();
    paint();
  }

  function handleRemove(si) {
    const sets = cards[current].card.sets;
    if (sets[si]?.done) return; // never silently drop a logged set
    sets.splice(si, 1);
    recomputeResult(cards[current]);
    persist();
    paint();
  }

  // ── Edit today's workout (plan v4 item 3) ─────────────────────────────────────
  // Every change asks "just today or keep it". Logged sets are never lost: they live on
  // their own lift's card, which moves with the lift.
  const keepAsk = (title) => chooseBox({ title, options: [{ id: 'today', label: 'Just today' }, { id: 'keep', label: 'Keep it in my plan' }] });
  const planIds = () => cards.map((e) => e.pe.id).filter(Boolean);

  async function handleAddLift() {
    openExercisePicker({
      title: 'Add a lift to today',
      usedIds: new Set(cards.map((e) => e.pe.exercise_id)),
      onPick: async (ex, custom) => {
        const scope = await keepAsk(`Add ${displayName(ex) || ex.name}`);
        if (!scope) return;
        const fields = {
          exercise_id: ex.id, sort: cards.length, target_sets: custom?.sets ?? 3,
          rep_low: custom?.lo ?? ex.default_rep_low ?? 6, rep_high: custom?.hi ?? ex.default_rep_high ?? 8,
          rest_seconds: custom?.rest ?? (ex.is_primary ? 180 : 120), increment_lb: custom?.inc ?? ex.default_increment_lb ?? 5, progression_mode: '1',
        };
        let pe = { ...fields, id: null, program_day_id: dayId, exercise: ex, tracking: ex.tracking || 'weight', knee: !!ex.knee_sensitive, start_weight_lb: null };
        if (scope === 'keep') {
          const row = await addProgramExercise(dayId, fields);
          if (!row) { toast("Couldn't add it to your plan, added for today", { tone: 'bad' }); }
          else pe = { ...row, exercise: ex, tracking: ex.tracking || 'weight', knee: !!ex.knee_sensitive };
        }
        const last = await loadLastSets(ex.id);
        const card = buildExerciseCard({ ...pe, exercise: ex }, last, { deload: !!phase?.deload });
        card.name = displayName(ex) || card.name;
        cards.splice(current + 1, 0, { pe, card, result: null, swappedFrom: null, kneeFeel: null, askRir: null, addedToday: scope === 'today' });
        current += 1;
        if (scope === 'keep') reorderProgramExercises(planIds());
        persist(); paint();
        toast(scope === 'keep' ? 'Added to today and your plan' : 'Added for today');
      },
    });
  }

  async function handleMore() {
    const e = cards[current];
    const hasLogged = e.card.sets.some((x) => x.done);
    const opts = [];
    if (current > 0) opts.push({ id: 'up', label: 'Move earlier' });
    if (current < cards.length - 1) opts.push({ id: 'down', label: 'Move later' });
    if (!hasLogged && cards.length > 1) opts.push({ id: 'skip', label: 'Skip this lift' });
    if (!opts.length) { toast(hasLogged ? 'Logged sets stay; this lift can still move' : 'Nothing to change here', { tone: 'info' }); return; }
    const pick = await chooseBox({ title: e.card.name, message: hasLogged ? 'Sets you logged stay with this lift.' : '', options: opts });
    if (!pick) return;
    if (pick === 'skip') return handleSkip();
    const to = pick === 'up' ? current - 1 : current + 1;
    const scope = (e.pe.id && cards[to].pe.id) ? await keepAsk('Move it') : 'today';
    if (!scope) return;
    [cards[current], cards[to]] = [cards[to], cards[current]];
    current = to;
    persist(); paint();
    if (scope === 'keep') { const ok = await reorderProgramExercises(planIds()); if (!ok) toast("Moved for today; your plan didn't save", { tone: 'bad' }); }
  }

  async function handleSkip() {
    const e = cards[current];
    if (e.card.sets.some((x) => x.done)) return; // never drop logged sets
    const scope = e.pe.id ? await keepAsk('Skip it') : 'today';
    if (!scope) return;
    if (scope === 'keep') {
      const r = await deleteProgramExercise(e.pe.id);
      if (r?.error) { toast("Couldn't remove it from your plan", { tone: 'bad' }); return; }
    }
    cards.splice(current, 1);
    current = Math.min(current, cards.length - 1);
    persist(); paint();
    toast(scope === 'keep' ? 'Removed from your plan' : 'Skipped for today');
  }

  // Tap the reps and rest on a lift's card to change them. Logged sets stay as they are.
  function handleEditLift() {
    const entry = cards[current];
    const c = entry.card;
    openLiftForm({
      mode: 'edit', title: c.name, saveLabel: 'Save', tracking: c.tracking || 'weight', name: c.name,
      muscle: entry.pe.exercise?.muscle_group, sets: entry.pe.target_sets, lo: c.repLow, hi: c.repHigh, rest: c.restSeconds, inc: c.increment,
      onSave: async (v) => {
        // A lift swapped in just for today isn't the one in the plan, so it can only change today.
        const inPlan = entry.pe.id && !entry.addedToday && (!entry.swappedFrom || entry.madeDefault);
        const scope = inPlan ? await keepAsk('Change it') : 'today';
        if (!scope) return;
        const fields = { rep_low: v.lo, rep_high: v.hi, rest_seconds: v.rest, ...(c.tracking === 'weight' ? { increment_lb: v.inc } : {}) };
        entry.pe = { ...entry.pe, ...fields };
        const fresh = buildExerciseCard({ ...entry.pe, exercise: entry.pe.exercise }, await loadLastSets(entry.pe.exercise_id), { deload: !!phase?.deload });
        c.repLow = v.lo; c.repHigh = v.hi; c.restSeconds = v.rest; c.coach = fresh.coach;
        if (c.tracking === 'weight') c.increment = v.inc;
        recomputeResult(entry);
        persist(); paint();
        if (scope === 'keep') {
          const ok = await updateProgramExercise(entry.pe.id, fields);
          toast(ok ? 'Saved to today and your plan' : "Changed for today; your plan didn't save", ok ? {} : { tone: 'bad' });
        } else toast('Changed for today');
      },
    });
  }

  function handleSwap() {
    const entry = cards[current];
    openSwap(entry.pe, async (opt, makeDefault, custom) => {
      const peId = entry.pe.id; // grit_program_exercises row (present for real program days)
      entry.swappedFrom = entry.pe.exercise_id;
      // A like-for-like swap keeps the slot's reps and rest (p.320). A different kind of lift or a
      // bodyweight/timed one uses its own; a written-in lift uses the numbers typed for it.
      const keepRange = (opt.tracking || 'weight') === 'weight' && (entry.pe.tracking || 'weight') === 'weight'
        && !!opt.is_primary === !!entry.pe.exercise?.is_primary;
      entry.pe = { ...entry.pe, exercise_id: opt.id, exercise: opt, tracking: opt.tracking,
        target_sets: custom?.sets ?? entry.pe.target_sets,
        rep_low: custom?.lo ?? (keepRange ? entry.pe.rep_low : opt.default_rep_low), rep_high: custom?.hi ?? (keepRange ? entry.pe.rep_high : opt.default_rep_high),
        rest_seconds: custom?.rest ?? (keepRange ? entry.pe.rest_seconds : (opt.is_primary ? 180 : 120)),
        increment_lb: custom?.inc ?? opt.default_increment_lb, knee: opt.knee_sensitive,
        start_weight_lb: null, carry_from_lb: null, progression_mode: '1' };
      entry.madeDefault = !!(makeDefault && peId);
      if (makeDefault && peId) {
        const ok = await updateProgramExercise(peId, { exercise_id: opt.id, target_sets: entry.pe.target_sets, rest_seconds: entry.pe.rest_seconds, rep_low: entry.pe.rep_low, rep_high: entry.pe.rep_high, increment_lb: entry.pe.increment_lb, start_weight_lb: null, carry_from_lb: null, progression_mode: '1' });
        if (ok) toast(`Made ${displayName(opt)} your default`);
        else toast("Couldn't set default, kept it for today", { tone: 'bad' });
      }
      const last = await loadLastSets(opt.id);
      entry.card = buildExerciseCard({ ...entry.pe, exercise: opt }, last, { deload: !!phase?.deload });
      entry.card.name = displayName(opt) || entry.card.name; // resolve personal name
      entry.result = null; entry.kneeFeel = null; entry.askRir = null; entry.savedEngine = null;
      // surface any note attached to the new lift
      notesByEx = notesByLift(await loadLiftNotes());
      persist();
      paint();
    });
  }

  async function handleFinish({ confirm: doConfirm } = {}) {
    const total = cards.reduce((n, e) => n + workSetsOf(e).length, 0);
    const done = cards.reduce((n, e) => n + workSetsOf(e).filter((s) => s.done).length, 0);
    if (doConfirm && done > 0 && done < total && !(await confirmBox({ title: 'Finish anyway?', message: `You have ${total - done} set${total - done === 1 ? '' : 's'} not logged.`, confirmLabel: 'Finish' }))) return;

    stopRest();
    pauseTimer();
    finished = true;
    teardown();
    // retry any sets that are checked but failed to save
    if (sessionId) {
      for (const e of cards) {
        for (let i = 0; i < e.card.sets.length; i++) {
          const s = e.card.sets[i];
          if (s.done && !s.savedId) {
            s.savedId = await saveSet(sessionId, { exerciseId: e.pe.exercise_id, setNumber: i + 1, weight: s.weight, reps: s.reps, rir: s.rir, isWarmup: s.isWarmup, swappedFrom: e.swappedFrom });
          }
        }
      }
    }
    const anyDone = cards.some((e) => e.card.sets.some((s) => s.done));
    const mins = Math.max(1, Math.round(totalElapsed() / 60000));

    if (sessionId && !anyDone) { await deleteSession(sessionId); clearProgress(); go('home'); return; }
    if (!sessionId) { clearProgress(); go('home'); return; }

    // Mark complete with retry. Gym signal is flaky and the OLD code did a single
    // unchecked write, so one dropped request left the session stuck on "partial".
    let finalized = false;
    for (let attempt = 0; attempt < 3 && !finalized; attempt++) {
      finalized = await finishSession(sessionId, mins, sessionNote);
      if (!finalized && attempt < 2) await new Promise((r) => setTimeout(r, 700));
    }
    if (!finalized) {
      // Keep the workout resumable so the finish can land later. Never lose it.
      toast("Saved your sets, but the finish didn't go through. Reconnect and tap Resume to wrap it up.", { tone: 'bad' });
      go('home');
      return;
    }
    clearProgress();
    showCelebration();
  }

  // Finish celebration (item 5): summary + aggregated gains before routing home.
  function showCelebration() {
    const sum = summarizeFinish(cards);
    const mins = Math.max(1, Math.round(totalElapsed() / 60000));
    const dayOne = cards.length > 0 && cards.every((e) => e.card.lastSummary === 'first time');

    const goUpList = sum.goUps.length
      ? `<div class="mt-2 space-y-1">${sum.goUps.map((g) => `<div class="flex items-center justify-between text-[15px]"><span class="text-ink font-semibold">${escapeHtml(g.name)}</span><span class="text-success font-bold">+${g.lb} lb</span></div>`).join('')}</div>`
      : '';

    const earnedCard = sum.headline
      ? `<div class="grit-card p-4 mt-5 w-full bg-success-tint border-0"><div class="text-success font-bold text-[15px]">${sum.headline}</div>${goUpList}</div>`
      : `<div class="grit-card p-4 mt-5 w-full"><div class="text-ink font-bold text-[15px]">No jump today. Next time, beat today's total reps by at least one (p.345).</div></div>`;

    el.innerHTML = `
      ${renderHeader()}
      <div class="grit-page px-5 pt-10 flex flex-col items-center text-center" style="min-height:72vh">
        <div class="w-16 h-16 rounded-full bg-success text-white flex items-center justify-center mb-4">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h1 class="grit-title">${dayOne ? 'Day one, done.' : 'Workout complete'}</h1>
        <p class="text-[15px] text-inksoft mt-2 max-w-xs leading-relaxed">${dayOne ? "First one's in the books. Showing up is the whole game, you just did the hardest rep." : 'Logged and saved. Same time, more weight, next time.'}</p>
        <div class="flex items-center gap-2 mt-5">
          <span class="grit-chip-accent">${sum.exercisesDone} lift${sum.exercisesDone === 1 ? '' : 's'}</span>
          <span class="grit-chip-success">${sum.workSets} set${sum.workSets === 1 ? '' : 's'}</span>
          <span class="grit-chip bg-fill text-ink">${mins} min</span>
        </div>
        ${sum.prs.length ? `<div class="grit-chip mt-3 bg-[#FBF3E0] text-[#7A570F]">${icon.check('w-4 h-4')} ${sum.prs.length} new personal best${sum.prs.length > 1 ? 's' : ''}</div>` : ''}
        ${earnedCard}
        <button id="cel-done" class="grit-btn-dark mt-6">Done</button>
        <div class="h-24"></div>
      </div>`;
    document.getElementById('cel-done').onclick = () => go('home');
  }
}
